import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Dashboard.css';
import './Admin.css';

// Markdown formatter for Copilot AI suggestions
function renderMarkdown(text) {
  if (!text) return "";
  let html = text;

  // Escape HTML entities
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Bold text: **bold**
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Code Blocks: ```lang ... ```
  html = html.replace(/```([\s\S]*?)```/g, (match, codeBlock) => {
    const lines = codeBlock.trim().split('\n');
    let lang = "";
    let code = codeBlock;
    if (lines[0].length < 15 && !lines[0].includes(' ') && lines[0].match(/^[a-zA-Z0-9+#]+$/)) {
      lang = lines[0];
      code = lines.slice(1).join('\n');
    }
    return `<pre><code class="language-${lang}">${code.trim()}</code></pre>`;
  });

  // Inline Code: `code`
  html = html.replace(/`(.*?)`/g, '<code>$1</code>');

  // Bullets: * or -
  html = html.replace(/^\s*[*+-]\s+(.*?)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*?<\/li>)+/g, '<ul>$&</ul>');

  // Format paragraphs
  const sections = html.split('</pre>');
  for (let i = 0; i < sections.length; i++) {
    const parts = sections[i].split('<pre>');
    parts[0] = parts[0]
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => line.startsWith('<ul>') || line.startsWith('<li>') || line.startsWith('</ul>') ? line : `<p>${line}</p>`)
      .join('');
    sections[i] = parts.join('<pre>');
  }

  return html;
}

// Ensure screenshot base64 strings have proper URI scheme
function formatImageSrc(imgStr) {
  if (!imgStr || typeof imgStr !== 'string') return '';
  const trimmed = imgStr.trim();
  if (
    trimmed.startsWith('data:image/') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }
  if (trimmed.startsWith('/9j/')) {
    return `data:image/jpeg;base64,${trimmed}`;
  }
  return `data:image/png;base64,${trimmed}`;
}

// Extract all valid screenshots from a transcript entry
function getEntryScreenshots(entry) {
  if (!entry) return [];
  const list = [];
  if (Array.isArray(entry.screenshots) && entry.screenshots.length > 0) {
    entry.screenshots.forEach(s => {
      if (s) list.push(s);
    });
  } else if (typeof entry.screenshots === 'string' && entry.screenshots.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(entry.screenshots);
      if (Array.isArray(parsed)) {
        parsed.forEach(s => { if (s) list.push(s); });
      }
    } catch (e) {}
  }
  if (list.length === 0 && entry.screenshot) {
    list.push(entry.screenshot);
  }
  return list.map(formatImageSrc).filter(Boolean);
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4310';

const Admin = ({ user, onLogout, onError, onSuccess }) => {
  const navigate = useNavigate();
  const validAdminTabs = ['overview', 'users', 'interviews', 'plans', 'payments', 'config'];

  // Initialize activeTab from URL search param ?tab= or localStorage or default 'overview'
  const [activeTab, setActiveTabState] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlTab = params.get('tab');
      if (urlTab && validAdminTabs.includes(urlTab)) {
        return urlTab;
      }
      const savedTab = localStorage.getItem('crackit_admin_tab');
      if (savedTab && validAdminTabs.includes(savedTab)) {
        return savedTab;
      }
    } catch (e) {}
    return 'overview';
  });

  const setActiveTab = useCallback((newTab) => {
    setActiveTabState(newTab);
    try {
      localStorage.setItem('crackit_admin_tab', newTab);
      const url = new URL(window.location.href);
      if (url.searchParams.get('tab') !== newTab) {
        url.searchParams.set('tab', newTab);
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    } catch (e) {}
  }, []);

  // Sync tab on browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const urlTab = params.get('tab');
        if (urlTab && validAdminTabs.includes(urlTab)) {
          setActiveTabState(urlTab);
        }
      } catch (e) {}
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync initial URL with query param if it was loaded from localStorage or default
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('tab') !== activeTab) {
        url.searchParams.set('tab', activeTab);
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    } catch (e) {}
  }, [activeTab]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Theme state synced with localStorage (exact same key 'coprep_theme_mode')
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem('coprep_theme_mode') || 'dark';
    } catch (e) {
      return 'dark';
    }
  });

  const handleToggleTheme = () => {
    const nextTheme = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(nextTheme);
    try {
      localStorage.setItem('coprep_theme_mode', nextTheme);
    } catch (e) {}
  };

  useEffect(() => {
    document.body.classList.toggle('light-theme', themeMode === 'light');
    document.documentElement.setAttribute('data-theme', themeMode);
    return () => {
      document.body.classList.remove('light-theme');
    };
  }, [themeMode]);

  // Collapsible sidebar groups
  const [expandedGroups, setExpandedGroups] = useState({
    users: true,
    interviews: true,
    plans: true,
    config: true
  });

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Data States
  const [stats, setStats] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [userPagination, setUserPagination] = useState({ page: 1, limit: 12, total: 0, totalPages: 1 });
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');

  const [interviewsList, setInterviewsList] = useState([]);
  const [interviewPagination, setInterviewPagination] = useState({ page: 1, limit: 12, total: 0, totalPages: 1 });
  const [interviewSearch, setInterviewSearch] = useState('');
  const [interviewStatusFilter, setInterviewStatusFilter] = useState('all');
  const [interviewCategoryFilter, setInterviewCategoryFilter] = useState('all');

  const [plansList, setPlansList] = useState([]);
  const [paymentsList, setPaymentsList] = useState([]);
  const [paymentPagination, setPaymentPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 1 });
  
  const [systemConfigs, setSystemConfigs] = useState({
    default_signup_credits: 30,
    credits_per_minute: 1,
    promo_banner_text: '',
    default_ai_model: 'gpt4',
    maintenance_mode: false,
    windows_app_url: '',
    mac_app_url: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  // Modals
  const [selectedUserForCredits, setSelectedUserForCredits] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditMode, setCreditMode] = useState('add');
  const [creditReason, setCreditReason] = useState('');

  const [selectedInterviewForView, setSelectedInterviewForView] = useState(null);
  const [isFullscreenTranscript, setIsFullscreenTranscript] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedPlanForEdit, setSelectedPlanForEdit] = useState(null);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);

  // Keyboard shortcut listener to close Lightbox or Modal on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (previewImage) {
          setPreviewImage(null);
        } else if (selectedInterviewForView) {
          setSelectedInterviewForView(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewImage, selectedInterviewForView]);

  const token = localStorage.getItem('token');

  // Authenticated API fetch helper
  const adminFetch = useCallback(async (endpoint, options = {}) => {
    const res = await fetch(`${API_URL}/api/admin${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(options.headers || {})
      }
    });
    return res.json();
  }, [token]);

  // Load Stats
  const loadStats = useCallback(async () => {
    try {
      const data = await adminFetch('/stats');
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, [adminFetch]);

  // Load Users
  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams({
        page: userPagination.page,
        limit: userPagination.limit,
        search: userSearch,
        role: userRoleFilter,
        status: userStatusFilter
      });
      const data = await adminFetch(`/users?${query.toString()}`);
      if (data.success) {
        setUsersList(data.users);
        setUserPagination(data.pagination);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch, userPagination.page, userPagination.limit, userSearch, userRoleFilter, userStatusFilter]);

  // Load Interviews
  const loadInterviews = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams({
        page: interviewPagination.page,
        limit: interviewPagination.limit,
        search: interviewSearch,
        status: interviewStatusFilter,
        category: interviewCategoryFilter
      });
      const data = await adminFetch(`/interviews?${query.toString()}`);
      if (data.success) {
        setInterviewsList(data.interviews);
        setInterviewPagination(data.pagination);
      }
    } catch (err) {
      console.error('Error loading interviews:', err);
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch, interviewPagination.page, interviewPagination.limit, interviewSearch, interviewStatusFilter, interviewCategoryFilter]);

  // Load Plans
  const loadPlans = useCallback(async () => {
    try {
      const data = await adminFetch('/plans');
      if (data.success) {
        setPlansList(data.plans);
      }
    } catch (err) {
      console.error('Error loading plans:', err);
    }
  }, [adminFetch]);

  // Load Payments
  const loadPayments = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams({
        page: paymentPagination.page,
        limit: paymentPagination.limit
      });
      const data = await adminFetch(`/payments?${query.toString()}`);
      if (data.success) {
        setPaymentsList(data.payments);
        setPaymentPagination(data.pagination);
      }
    } catch (err) {
      console.error('Error loading payments:', err);
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch, paymentPagination.page, paymentPagination.limit]);

  // Load Configs
  const loadConfigs = useCallback(async () => {
    try {
      const data = await adminFetch('/config');
      if (data.success) {
        setSystemConfigs(data.configs);
      }
    } catch (err) {
      console.error('Error loading configs:', err);
    }
  }, [adminFetch]);

  // Tab change trigger
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    if (activeTab === 'overview') { loadStats(); loadUsers(); }
    else if (activeTab === 'users') loadUsers();
    else if (activeTab === 'interviews') loadInterviews();
    else if (activeTab === 'plans') loadPlans();
    else if (activeTab === 'payments') loadPayments();
    else if (activeTab === 'config') loadConfigs();
  }, [activeTab, user, loadStats, loadUsers, loadInterviews, loadPlans, loadPayments, loadConfigs]);

  // Action: Save User Credits
  const handleSaveCredits = async () => {
    if (!selectedUserForCredits || !creditAmount) return;
    try {
      const data = await adminFetch(`/users/${selectedUserForCredits._id}/credits`, {
        method: 'PUT',
        body: JSON.stringify({
          amount: Number(creditAmount),
          mode: creditMode,
          reason: creditReason
        })
      });
      if (data.success) {
        if (onSuccess) onSuccess(`Credits updated for ${selectedUserForCredits.name}`);
        setSelectedUserForCredits(null);
        setCreditAmount('');
        setCreditReason('');
        loadUsers();
        loadStats();
      } else {
        if (onError) onError(data.message || 'Failed to update credits');
      }
    } catch (err) {
      if (onError) onError('Network error adjusting credits');
    }
  };

  // Action: Toggle Role
  const handleToggleRole = async (targetUser) => {
    const nextRole = targetUser.role === 'admin' ? 'user' : 'admin';
    try {
      const data = await adminFetch(`/users/${targetUser._id}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: nextRole })
      });
      if (data.success) {
        if (onSuccess) onSuccess(`Role updated to ${nextRole} for ${targetUser.email}`);
        loadUsers();
      } else {
        if (onError) onError(data.message || 'Failed to update role');
      }
    } catch (err) {
      if (onError) onError('Network error updating role');
    }
  };

  // Action: Toggle Status
  const handleToggleStatus = async (targetUser) => {
    const nextStatus = targetUser.status === 'suspended' ? 'active' : 'suspended';
    try {
      const data = await adminFetch(`/users/${targetUser._id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: nextStatus })
      });
      if (data.success) {
        if (onSuccess) onSuccess(`Account status marked as ${nextStatus}`);
        loadUsers();
      } else {
        if (onError) onError(data.message || 'Failed to update status');
      }
    } catch (err) {
      if (onError) onError('Network error updating status');
    }
  };

  // Action: Terminate Interview
  const handleTerminateInterview = async (interviewId) => {
    try {
      const data = await adminFetch(`/interviews/${interviewId}/terminate`, { method: 'PUT' });
      if (data.success) {
        if (onSuccess) onSuccess('Session terminated successfully');
        loadInterviews();
        loadStats();
      }
    } catch (err) {
      if (onError) onError('Failed to terminate interview');
    }
  };

  // Action: Delete Target
  const handleConfirmDelete = async () => {
    if (!deleteConfirmTarget) return;
    try {
      let endpoint = '';
      if (deleteConfirmTarget.type === 'user') endpoint = `/users/${deleteConfirmTarget.id}`;
      else if (deleteConfirmTarget.type === 'interview') endpoint = `/interviews/${deleteConfirmTarget.id}`;
      else if (deleteConfirmTarget.type === 'plan') endpoint = `/plans/${deleteConfirmTarget.id}`;

      const data = await adminFetch(endpoint, { method: 'DELETE' });
      if (data.success) {
        if (onSuccess) onSuccess(data.message || 'Item deleted successfully');
        setDeleteConfirmTarget(null);
        if (deleteConfirmTarget.type === 'user') loadUsers();
        else if (deleteConfirmTarget.type === 'interview') loadInterviews();
        else if (deleteConfirmTarget.type === 'plan') loadPlans();
        loadStats();
      } else {
        if (onError) onError(data.message || 'Failed to delete item');
      }
    } catch (err) {
      if (onError) onError('Network error performing deletion');
    }
  };

  // Action: Save Plan
  const handleSavePlan = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const rawBadge = formData.get('badge');
    const planPayload = {
      name: formData.get('name'),
      price: Number(formData.get('price')),
      credits: Number(formData.get('credits')),
      badge: rawBadge && rawBadge.trim() ? rawBadge.trim() : null,
      isPopular: formData.get('isPopular') === 'on',
      isActive: formData.get('isActive') === 'on',
      displayOrder: Number(formData.get('displayOrder')) || 0
    };

    try {
      let data;
      if (isCreatingPlan) {
        data = await adminFetch('/plans', {
          method: 'POST',
          body: JSON.stringify(planPayload)
        });
      } else {
        data = await adminFetch(`/plans/${selectedPlanForEdit._id}`, {
          method: 'PUT',
          body: JSON.stringify(planPayload)
        });
      }

      if (data.success) {
        if (onSuccess) onSuccess(data.message || 'Plan saved successfully');
        setIsCreatingPlan(false);
        setSelectedPlanForEdit(null);
        loadPlans();
      } else {
        if (onError) onError(data.message || 'Failed to save plan');
      }
    } catch (err) {
      if (onError) onError('Error saving pricing plan');
    }
  };

  // Action: Save System Configs
  const handleSaveConfigs = async (e) => {
    e.preventDefault();
    try {
      const data = await adminFetch('/config', {
        method: 'PUT',
        body: JSON.stringify({ configs: systemConfigs })
      });
      if (data.success) {
        if (onSuccess) onSuccess('System configurations saved successfully');
      } else {
        if (onError) onError(data.message || 'Failed to save configs');
      }
    } catch (err) {
      if (onError) onError('Error updating system configurations');
    }
  };

  // View full transcript
  const handleViewTranscript = async (interviewId) => {
    try {
      const data = await adminFetch(`/interviews/${interviewId}`);
      if (data.success) {
        setSelectedInterviewForView(data.interview);
        setIsFullscreenTranscript(true);
      }
    } catch (err) {
      if (onError) onError('Failed to load session transcript');
    }
  };

  // Helpers
  const getUserDisplayName = () => user?.name || user?.email?.split('@')[0] || 'Administrator';
  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'AD';
  };

  // Guard: Unauthorized
  if (!user || user.role !== 'admin') {
    return (
      <div className={`coprep-dashboard-app admin-dashboard-app ${themeMode === 'dark' ? 'dark-theme' : 'light-theme'}`} style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="coprep-modal-card" style={{ maxWidth: '460px', textAlign: 'center', padding: '36px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', margin: '0 auto 16px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0 0 8px' }}>Admin Privileges Required</h2>
          <p style={{ color: 'var(--dash-text-muted)', marginBottom: '24px' }}>
            Your account does not have administrator permissions to view this control panel.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button className="start-assistant-primary-btn" onClick={() => navigate('/dashboard')}>
              Go to Candidate Dashboard
            </button>
            <button className="modal-secondary-btn" onClick={() => navigate('/')}>
              Main Website
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`coprep-dashboard-app admin-dashboard-app ${themeMode === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      
      {/* ====================================================================
          1. EXACT REPLICA FIXED SIDEBAR
         ==================================================================== */}
      <aside className={`coprep-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
        
        {/* Brand Logo Header */}
        <div className="sidebar-top-brand">
          <div className="sidebar-brand-link" onClick={() => setActiveTab('overview')}>
            <div className="brand-logo-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <span className="brand-logo-title">CrackIT<span className="brand-ai-badge">.AI</span></span>
            <span className="admin-badge-tag">ADMIN</span>
          </div>
        </div>

        {/* Sidebar Nav Items */}
        <div className="sidebar-menu-scrollable">
          
          {/* Overview Nav Button */}
          <button 
            className={`sidebar-nav-btn ${activeTab === 'overview' ? 'active-nav-item' : ''}`}
            onClick={() => {
              setActiveTab('overview');
              setMobileSidebarOpen(false);
            }}
          >
            <span className="nav-btn-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="7" height="9" x="3" y="3" rx="1"/>
                <rect width="7" height="5" x="14" y="3" rx="1"/>
                <rect width="7" height="9" x="14" y="12" rx="1"/>
                <rect width="7" height="5" x="3" y="16" rx="1"/>
              </svg>
            </span>
            <span className="nav-btn-text">Overview</span>
          </button>

          {/* Group 1: User Management */}
          <div className="sidebar-group-item">
            <button 
              className="sidebar-group-toggle"
              onClick={() => toggleGroup('users')}
            >
              <div className="group-toggle-left">
                <span className="group-toggle-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </span>
                <span className="group-toggle-text">User Accounts</span>
              </div>
              <div className="group-toggle-right">
                <span className={`toggle-chevron ${expandedGroups.users ? 'expanded' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </div>
            </button>

            {expandedGroups.users && (
              <div className="sidebar-sub-menu">
                <button 
                  className={`sidebar-sub-item ${activeTab === 'users' ? 'active-sub-item' : ''}`}
                  onClick={() => {
                    setActiveTab('users');
                    setMobileSidebarOpen(false);
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <circle cx="12" cy="10" r="3"/>
                      <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">All Candidates</span>
                  {stats?.totalUsers > 0 && (
                    <span className="session-count-badge">{stats.totalUsers}</span>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Group 2: Interview Sessions */}
          <div className="sidebar-group-item">
            <button 
              className="sidebar-group-toggle"
              onClick={() => toggleGroup('interviews')}
            >
              <div className="group-toggle-left">
                <span className="group-toggle-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="6"/>
                    <circle cx="12" cy="12" r="2"/>
                  </svg>
                </span>
                <span className="group-toggle-text">Live Sessions</span>
              </div>
              <div className="group-toggle-right">
                <span className={`toggle-chevron ${expandedGroups.interviews ? 'expanded' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </div>
            </button>

            {expandedGroups.interviews && (
              <div className="sidebar-sub-menu">
                <button 
                  className={`sidebar-sub-item ${activeTab === 'interviews' ? 'active-sub-item' : ''}`}
                  onClick={() => {
                    setActiveTab('interviews');
                    setMobileSidebarOpen(false);
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Sessions Inspector</span>
                  {stats?.activeInterviews > 0 ? (
                    <span className="session-count-badge active-live-badge">{stats.activeInterviews} live</span>
                  ) : (
                    stats?.totalInterviews > 0 && <span className="session-count-badge">{stats.totalInterviews}</span>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Group 3: Store & Billing */}
          <div className="sidebar-group-item">
            <button 
              className="sidebar-group-toggle"
              onClick={() => toggleGroup('plans')}
            >
              <div className="group-toggle-left">
                <span className="group-toggle-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="14" x="2" y="5" rx="2"/>
                    <line x1="2" y1="10" x2="22" y2="10"/>
                  </svg>
                </span>
                <span className="group-toggle-text">Store & Billing</span>
              </div>
              <div className="group-toggle-right">
                <span className={`toggle-chevron ${expandedGroups.plans ? 'expanded' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </div>
            </button>

            {expandedGroups.plans && (
              <div className="sidebar-sub-menu">
                <button 
                  className={`sidebar-sub-item ${activeTab === 'plans' ? 'active-sub-item' : ''}`}
                  onClick={() => {
                    setActiveTab('plans');
                    setMobileSidebarOpen(false);
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Pricing Packages</span>
                </button>

                <button 
                  className={`sidebar-sub-item ${activeTab === 'payments' ? 'active-sub-item' : ''}`}
                  onClick={() => {
                    setActiveTab('payments');
                    setMobileSidebarOpen(false);
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23"/>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Payment Ledger</span>
                </button>
              </div>
            )}
          </div>

          {/* Group 4: System & AI */}
          <div className="sidebar-group-item">
            <button 
              className="sidebar-group-toggle"
              onClick={() => toggleGroup('config')}
            >
              <div className="group-toggle-left">
                <span className="group-toggle-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </span>
                <span className="group-toggle-text">System & AI</span>
              </div>
              <div className="group-toggle-right">
                <span className={`toggle-chevron ${expandedGroups.config ? 'expanded' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </div>
            </button>

            {expandedGroups.config && (
              <div className="sidebar-sub-menu">
                <button 
                  className={`sidebar-sub-item ${activeTab === 'config' ? 'active-sub-item' : ''}`}
                  onClick={() => {
                    setActiveTab('config');
                    setMobileSidebarOpen(false);
                  }}
                >
                  <span className="sub-item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9"/>
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                    </svg>
                  </span>
                  <span className="sub-item-text">Global Configs</span>
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Sidebar Bottom Container */}
        <div className="sidebar-bottom-container">
          
          {/* Status Info Card */}
          <div className="sidebar-credits-card">
            <div className="credits-card-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span className="credits-card-title">System Status</span>
            </div>
            <p className="credits-card-desc">
              All 3 models online • Real-time latency ~180ms
            </p>
          </div>

          {/* User Dashboard Switch Button */}
          <button 
            className="sidebar-desktop-app-btn"
            onClick={() => navigate('/dashboard')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            <span>Candidate Dashboard</span>
          </button>

          {/* User Profile Pill with 3-dots */}
          <div className="sidebar-user-pill" ref={profileMenuRef}>
            <div className="user-avatar-circle">
              {getUserInitials()}
            </div>
            <div className="user-pill-info">
              <span className="user-pill-name">{getUserDisplayName()}</span>
              <span className="user-pill-email" title={user?.email}>{user?.email || 'admin@crackit.ai'}</span>
            </div>
            <button 
              className="user-pill-more-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              aria-label="Admin Menu"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="1.5"/>
                <circle cx="12" cy="5" r="1.5"/>
                <circle cx="12" cy="19" r="1.5"/>
              </svg>
            </button>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div className="sidebar-user-menu-dropdown">
                <div className="user-menu-header">
                  <span className="menu-user-credits">Role: ADMINISTRATOR</span>
                </div>
                <button 
                  className="user-menu-item"
                  onClick={() => {
                    navigate('/dashboard');
                    setShowProfileMenu(false);
                  }}
                >
                  Candidate Dashboard
                </button>
                <button 
                  className="user-menu-item"
                  onClick={() => {
                    navigate('/');
                    setShowProfileMenu(false);
                  }}
                >
                  Main Website
                </button>
                <div className="user-menu-divider"></div>
                <button 
                  className="user-menu-item text-danger"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>

        </div>

      </aside>

      {/* Mobile Backdrop */}
      {mobileSidebarOpen && (
        <div 
          className="sidebar-mobile-backdrop"
          onClick={() => setMobileSidebarOpen(false)}
        ></div>
      )}

      {/* ====================================================================
          2. EXACT REPLICA FIXED TOPBAR & MAIN STAGE
         ==================================================================== */}
      <main className="coprep-main-stage">
        
        {/* Top Navbar */}
        <header className="coprep-dashboard-topbar">
          <div className="topbar-left">
            <button 
              className="topbar-hamburger-btn"
              onClick={() => {
                if (window.innerWidth <= 900) {
                  setMobileSidebarOpen(!mobileSidebarOpen);
                } else {
                  setSidebarCollapsed(!sidebarCollapsed);
                }
              }}
              title="Toggle Menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="topbar-right">
            {/* Live Sessions Status Chip */}
            <div 
              className="topbar-credits-chip"
              onClick={() => setActiveTab('interviews')}
              title="Click to view live sessions"
            >
              <span className="chip-bolt">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              </span>
              <span className="chip-val">{stats?.activeInterviews || 0} Live Sessions</span>
            </div>

            {/* Candidate Dashboard quick button */}
            <button 
              className="topbar-icon-btn"
              onClick={() => navigate('/dashboard')}
              title="Switch to Candidate Dashboard"
              style={{ width: 'auto', padding: '0 12px', borderRadius: '20px', gap: '6px', fontSize: '0.82rem', fontWeight: 600 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              <span>User Dashboard</span>
            </button>

            {/* Exact Light / Dark Theme Switcher */}
            <button 
              className="topbar-icon-btn"
              onClick={handleToggleTheme}
              title={`Switch to ${themeMode === 'light' ? 'Dark' : 'Light'} Mode`}
            >
              {themeMode === 'light' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="4"/>
                  <path d="M12 2v2"/>
                  <path d="M12 20v2"/>
                  <path d="m4.93 4.93 1.41 1.41"/>
                  <path d="m17.66 17.66 1.41 1.41"/>
                  <path d="M2 12h2"/>
                  <path d="M20 12h2"/>
                  <path d="m6.34 17.66-1.41 1.41"/>
                  <path d="m19.07 4.93-1.41 1.41"/>
                </svg>
              )}
            </button>
          </div>
        </header>

        {/* Viewport Content Area */}
        <div className="dashboard-content-viewport">
          
          {/* ================================================================
              VIEW 1: OVERVIEW DASHBOARD
             ================================================================ */}
          {activeTab === 'overview' && (
            <div className="dashboard-overview-container">
              
              {/* 1. Hero Command Greeting Card */}
              <motion.div 
                className="dashboard-greeting-card"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="greeting-text-area">
                  <h1 className="greeting-main-title">
                    Platform Command Center
                  </h1>
                  <p className="greeting-subtitle">
                    Real-time monitoring of candidate audio streams, wallet balances, and platform operations.
                  </p>
                </div>

                <button 
                  className="start-assistant-primary-btn"
                  onClick={() => setActiveTab('interviews')}
                >
                  <span>Inspect Live Sessions</span>
                  <span className="btn-arrow">›</span>
                </button>
              </motion.div>

              {/* 2. 4 Metric Tiles Grid */}
              <div className="dashboard-metrics-grid">
                
                {/* Metric 1: Users */}
                <div 
                  className="metric-card-tile clickable-metric"
                  onClick={() => setActiveTab('users')}
                >
                  <div className="metric-header-row">
                    <div className="metric-icon-badge badge-green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                    </div>
                    <span className="metric-tag-label">Users</span>
                  </div>
                  <h3 className="metric-primary-val">{(stats?.totalUsers || userPagination?.total || usersList.length || 0)} Total</h3>
                  <span className="metric-status-link link-green">
                    {(stats?.activeUsers || usersList.filter(u => u.status !== 'suspended').length || 0)} Active accounts ›
                  </span>
                </div>

                {/* Metric 2: Sessions */}
                <div 
                  className="metric-card-tile clickable-metric"
                  onClick={() => setActiveTab('interviews')}
                >
                  <div className="metric-header-row">
                    <div className="metric-icon-badge badge-purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <circle cx="12" cy="12" r="6"/>
                        <circle cx="12" cy="12" r="2"/>
                      </svg>
                    </div>
                    <span className="metric-tag-label">Interviews</span>
                  </div>
                  <h3 className="metric-primary-val">{stats?.totalInterviews || 0} Total</h3>
                  <span className="metric-status-link link-purple">
                    {stats?.activeInterviews || 0} Live right now ›
                  </span>
                </div>

                {/* Metric 3: Revenue */}
                <div 
                  className="metric-card-tile clickable-metric"
                  onClick={() => setActiveTab('payments')}
                >
                  <div className="metric-header-row">
                    <div className="metric-icon-badge badge-cyan">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="1" x2="12" y2="23"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                    </div>
                    <span className="metric-tag-label">Revenue</span>
                  </div>
                  <h3 className="metric-primary-val">₹{(stats?.totalRevenueINR || 0).toLocaleString()}</h3>
                  <span className="metric-status-link link-cyan">
                    {stats?.totalPayments || 0} Paid Orders ›
                  </span>
                </div>

                {/* Metric 4: Circulating Credits */}
                <div 
                  className="metric-card-tile clickable-metric"
                  onClick={() => setActiveTab('config')}
                >
                  <div className="metric-header-row">
                    <div className="metric-icon-badge badge-pink">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    </div>
                    <span className="metric-tag-label">Circulation</span>
                  </div>
                  <h3 className="metric-primary-val">
                    {Math.round(stats?.totalCirculatingCredits || usersList.reduce((sum, u) => sum + (u.credits || 0), 0) || 0).toLocaleString()} Cr
                  </h3>
                  <span className="metric-status-link link-pink">
                    In candidate balances ›
                  </span>
                </div>

              </div>

              {/* 3. 2-Column Bottom Cards Grid */}
              <div className="dashboard-bottom-grid">
                
                {/* Left Column: 7-Day Velocity Chart */}
                <div className="dashboard-sub-card weekly-activity-card">
                  <div className="card-top-row">
                    <div className="card-title-block">
                      <h4 className="card-heading">Platform Weekly Activity</h4>
                      <span className="card-subheading">Candidate signups vs interview sessions conducted</span>
                    </div>
                    <div className="card-right-metric">
                      <span className="big-num">{(stats?.totalUsers || userPagination?.total || usersList.length || 0)}</span>
                      <span className="lbl">Total Candidates</span>
                    </div>
                  </div>

                  <div className="weekly-chart-area">
                    <div className="chart-gridlines">
                      <div className="gridline-row"><span>10</span><div className="line"></div></div>
                      <div className="gridline-row"><span>8</span><div className="line"></div></div>
                      <div className="gridline-row"><span>6</span><div className="line"></div></div>
                      <div className="gridline-row"><span>4</span><div className="line"></div></div>
                      <div className="gridline-row"><span>0</span><div className="line"></div></div>
                    </div>

                    <div className="chart-bars-row">
                      {(stats?.activityChart?.days || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).map((day, idx) => {
                        const signupsVal = stats?.activityChart?.signups?.[idx] ?? (idx === 6 ? usersList.length || 3 : [1, 2, 0, 3, 1, 2][idx] || 1);
                        const sessionsVal = stats?.activityChart?.sessions?.[idx] ?? (stats?.totalInterviews ? 1 : [0, 1, 0, 2, 1, 2][idx] || 0);
                        return (
                          <div key={day} className="chart-column-item">
                            <div className="bar-track" style={{ display: 'flex', gap: '4px', alignItems: 'flex-end', justifyContent: 'center' }}>
                              <div 
                                title={`Signups: ${signupsVal}`}
                                style={{ 
                                  width: '8px', 
                                  height: `${Math.min(100, Math.max(12, signupsVal * 16))}%`, 
                                  background: 'linear-gradient(180deg, #00f0ff 0%, #0284c7 100%)',
                                  borderRadius: '4px',
                                  boxShadow: '0 0 10px rgba(0, 240, 255, 0.4)'
                                }}
                              />
                              <div 
                                title={`Sessions: ${sessionsVal}`}
                                style={{ 
                                  width: '8px', 
                                  height: `${Math.min(100, Math.max(8, sessionsVal * 16))}%`, 
                                  background: 'linear-gradient(180deg, #00ff66 0%, #059669 100%)',
                                  borderRadius: '4px',
                                  boxShadow: '0 0 10px rgba(0, 255, 102, 0.4)'
                                }}
                              />
                            </div>
                            <span className="day-label">{day}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="chart-legend-row">
                    <div className="legend-item">
                      <span className="legend-dot dot-cyan"></span>
                      <span>Candidate Signups</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot dot-green"></span>
                      <span>Interview Sessions</span>
                    </div>
                  </div>
                </div>

                {/* Right Column: Recent Candidates */}
                <div className="dashboard-sub-card">
                  <div className="card-top-row">
                    <div className="card-title-block">
                      <h4 className="card-heading">Recent Candidates</h4>
                      <span className="card-subheading">Recently registered accounts & balances</span>
                    </div>
                    <button className="read-more-link" onClick={() => setActiveTab('users')}>
                      View All ›
                    </button>
                  </div>

                  <div className="admin-candidates-list">
                    {usersList.slice(0, 5).map(u => {
                      const initials = (u.name || u.email || 'U')
                        .split(' ')
                        .filter(Boolean)
                        .map(n => n[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase();
                      return (
                        <div 
                          key={u._id} 
                          className="admin-candidate-card" 
                          onClick={() => setSelectedUserForCredits(u)}
                          title="Click to adjust candidate credits"
                        >
                          <div className="candidate-card-left">
                            <div className="candidate-avatar-circle">
                              {initials}
                            </div>
                            <div className="candidate-meta-block">
                              <div className="candidate-name-row">
                                <span className="candidate-name-text">{u.name || 'Candidate'}</span>
                                <span className={`candidate-role-tag ${u.role === 'admin' ? 'role-admin' : 'role-user'}`}>
                                  {u.role || 'user'}
                                </span>
                              </div>
                              <span className="candidate-email-text" title={u.email}>{u.email}</span>
                            </div>
                          </div>
                          <div className="candidate-card-right">
                            <span className="candidate-credits-chip">
                              {Math.round(u.credits || 0).toLocaleString()} Cr
                            </span>
                            <span className={`status-pill status-${u.status || 'active'}`}>
                              {u.status || 'active'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>

            </div>
          )}

          {/* ================================================================
              VIEW 2: CANDIDATE ACCOUNTS DIRECTORY
             ================================================================ */}
          {activeTab === 'users' && (
            <div>
              <div className="admin-view-header-row">
                <div className="header-title-block">
                  <h2>Candidate Directory & Accounts</h2>
                  <p>Search, adjust credit balances, manage roles, and monitor account status.</p>
                </div>

                <div className="admin-filters-bar">
                  <div className="admin-search-input-box">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input 
                      type="text" 
                      placeholder="Search candidates..." 
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                    />
                  </div>

                  <select 
                    className="admin-select-input"
                    value={userRoleFilter}
                    onChange={(e) => setUserRoleFilter(e.target.value)}
                  >
                    <option value="all">All Roles</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>

                  <select 
                    className="admin-select-input"
                    value={userStatusFilter}
                    onChange={(e) => setUserStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>

                  <button className="start-assistant-primary-btn" onClick={loadUsers} style={{ padding: '8px 16px', fontSize: '0.84rem' }}>
                    Refresh
                  </button>
                </div>
              </div>

              <div className="admin-table-card">
                <table className="admin-modern-table">
                  <thead>
                    <tr>
                      <th>Candidate</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Credits Balance</th>
                      <th>Joined Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.length === 0 ? (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: '36px', color: 'var(--dash-text-muted)' }}>No candidates found matching criteria.</td></tr>
                    ) : (
                      usersList.map(u => (
                        <tr key={u._id}>
                          <td>
                            <div className="table-user-cell">
                              <div className="table-user-avatar">{u.name?.charAt(0) || 'U'}</div>
                              <div className="table-user-info">
                                <span className="table-user-name">{u.name}</span>
                                <span className="table-user-email">{u.email}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`status-pill status-${u.role || 'user'}`}>
                              {u.role || 'user'}
                            </span>
                          </td>
                          <td>
                            <span className={`status-pill status-${u.status || 'active'}`}>
                              {u.status || 'active'}
                            </span>
                          </td>
                          <td>
                            <strong style={{ color: 'var(--dash-accent)', fontSize: '0.92rem' }}>{Math.round(u.credits || 0).toLocaleString()} Credits</strong>
                          </td>
                          <td>
                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td>
                            <div className="table-actions-group">
                              <button className="action-pill-btn" onClick={() => setSelectedUserForCredits(u)}>
                                Edit Credits
                              </button>
                              <button className="action-pill-btn" onClick={() => handleToggleRole(u)}>
                                {u.role === 'admin' ? 'Revoke Admin' : 'Make Admin'}
                              </button>
                              <button className="action-pill-btn" onClick={() => handleToggleStatus(u)}>
                                {u.status === 'suspended' ? 'Activate' : 'Suspend'}
                              </button>
                              <button className="action-pill-btn btn-danger" onClick={() => setDeleteConfirmTarget({ type: 'user', id: u._id, name: u.email })}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {userPagination.totalPages > 1 && (
                  <div className="admin-table-pagination">
                    <span>Page {userPagination.page} of {userPagination.totalPages} ({userPagination.total} candidates)</span>
                    <div className="pagination-btns-row">
                      <button 
                        className="pagination-nav-btn"
                        disabled={userPagination.page <= 1}
                        onClick={() => setUserPagination(p => ({ ...p, page: p.page - 1 }))}
                      >
                        ‹ Previous
                      </button>
                      <button 
                        className="pagination-nav-btn"
                        disabled={userPagination.page >= userPagination.totalPages}
                        onClick={() => setUserPagination(p => ({ ...p, page: p.page + 1 }))}
                      >
                        Next ›
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ================================================================
              VIEW 3: LIVE SESSIONS INSPECTOR
             ================================================================ */}
          {activeTab === 'interviews' && (
            <div>
              <div className="admin-view-header-row">
                <div className="header-title-block">
                  <h2>Interview Sessions Inspector</h2>
                  <p>Inspect speech transcriptions, screenshot OCR, and force-end live sessions.</p>
                </div>

                <div className="admin-filters-bar">
                  <div className="admin-search-input-box">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input 
                      type="text" 
                      placeholder="Search session ID..." 
                      value={interviewSearch}
                      onChange={(e) => setInterviewSearch(e.target.value)}
                    />
                  </div>

                  <select 
                    className="admin-select-input"
                    value={interviewStatusFilter}
                    onChange={(e) => setInterviewStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active (Live)</option>
                    <option value="completed">Completed</option>
                  </select>

                  <select 
                    className="admin-select-input"
                    value={interviewCategoryFilter}
                    onChange={(e) => setInterviewCategoryFilter(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    <option value="technical">Technical Coding</option>
                    <option value="behavioral">Behavioral</option>
                    <option value="hr">HR Screen</option>
                    <option value="negotiation">Offer Negotiation</option>
                  </select>

                  <button className="start-assistant-primary-btn" onClick={loadInterviews} style={{ padding: '8px 16px', fontSize: '0.84rem' }}>
                    Refresh
                  </button>
                </div>
              </div>

              <div className="admin-table-card">
                <table className="admin-modern-table">
                  <thead>
                    <tr>
                      <th>Session ID</th>
                      <th>Candidate</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Duration / Tokens</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interviewsList.length === 0 ? (
                      <tr><td colSpan="7" style={{ textAlign: 'center', padding: '36px', color: 'var(--dash-text-muted)' }}>No interview sessions logged.</td></tr>
                    ) : (
                      interviewsList.map(int => (
                        <tr key={int._id}>
                          <td>
                            <strong style={{ fontFamily: 'monospace', color: 'var(--dash-accent)' }}>
                              {int.sessionId}
                            </strong>
                          </td>
                          <td>
                            <div className="table-user-info">
                              <span className="table-user-name">{int.userId?.name || 'Candidate'}</span>
                              <span className="table-user-email">{int.userId?.email || 'N/A'}</span>
                            </div>
                          </td>
                          <td>
                            <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                              {int.config?.interviewType || 'technical'}
                            </span>
                          </td>
                          <td>
                            <span className={`status-pill status-${int.status}`}>
                              {int.status}
                            </span>
                          </td>
                          <td>
                            <div>
                              <span>{int.duration ? `${Math.ceil(int.duration / 60)} min` : '< 1 min'}</span>
                              <span style={{ display: 'block', fontSize: '0.74rem', color: 'var(--dash-text-muted)' }}>
                                {Math.round(int.tokensConsumed || 0).toLocaleString()} Credits
                              </span>
                            </div>
                          </td>
                          <td>
                            {int.startedAt ? new Date(int.startedAt).toLocaleString() : 'N/A'}
                          </td>
                          <td>
                            <div className="table-actions-group">
                              <button className="action-pill-btn" onClick={() => handleViewTranscript(int._id)}>
                                View Transcripts
                              </button>
                              {int.status === 'active' && (
                                <button className="action-pill-btn btn-danger" onClick={() => handleTerminateInterview(int._id)}>
                                  Force End
                                </button>
                              )}
                              <button className="action-pill-btn btn-danger" onClick={() => setDeleteConfirmTarget({ type: 'interview', id: int._id, name: int.sessionId })}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {interviewPagination.totalPages > 1 && (
                  <div className="admin-table-pagination">
                    <span>Page {interviewPagination.page} of {interviewPagination.totalPages}</span>
                    <div className="pagination-btns-row">
                      <button 
                        className="pagination-nav-btn"
                        disabled={interviewPagination.page <= 1}
                        onClick={() => setInterviewPagination(p => ({ ...p, page: p.page - 1 }))}
                      >
                        ‹ Prev
                      </button>
                      <button 
                        className="pagination-nav-btn"
                        disabled={interviewPagination.page >= interviewPagination.totalPages}
                        onClick={() => setInterviewPagination(p => ({ ...p, page: p.page + 1 }))}
                      >
                        Next ›
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ================================================================
              VIEW 4: PRICING PACKAGES & STORE (Exact User Store Layout)
             ================================================================ */}
          {activeTab === 'plans' && (
            <div>
              <div className="admin-view-header-row">
                <div className="header-title-block">
                  <h2>Pricing Plans & Credit Store</h2>
                  <p>Configure plan prices, credits allocation, badges, and store availability.</p>
                </div>

                <button 
                  className="start-assistant-primary-btn"
                  onClick={() => { setIsCreatingPlan(true); setSelectedPlanForEdit(null); }}
                >
                  + Add New Plan
                </button>
              </div>

              <div className="store-pricing-grid">
                {plansList.map(plan => (
                  <div key={plan._id} className={`store-plan-card ${plan.isPopular || plan.badge ? 'popular-plan' : ''}`}>
                    {(plan.badge || plan.isPopular) && <div className="popular-ribbon">{plan.badge || 'MOST POPULAR'}</div>}
                    
                    <h3 className="plan-name">{plan.name}</h3>
                    <p className="plan-desc">
                      {plan.duration?.hours || 0} Hours of Live Interview Copilot ({Math.round(plan.credits || 0).toLocaleString()} Credits)
                    </p>

                    <div className="plan-price-block">
                      <span className="currency">₹</span>
                      <span className="price-val">{plan.price}</span>
                      <span className="one-time">one-time</span>
                    </div>

                    <div className="plan-credits-callout">
                      <strong>{Math.round(plan.credits || 0).toLocaleString()} Copilot Credits</strong>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '16px' }}>
                      <button 
                        className="plan-buy-btn buy-btn-primary" 
                        style={{ flex: 1 }}
                        onClick={() => { setSelectedPlanForEdit(plan); setIsCreatingPlan(false); }}
                      >
                        Edit Plan
                      </button>
                      <button 
                        className="plan-buy-btn buy-btn-secondary"
                        style={{ width: 'auto', padding: '0 16px', color: '#ef4444' }}
                        onClick={() => setDeleteConfirmTarget({ type: 'plan', id: plan._id, name: plan.name })}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ================================================================
              VIEW 5: PAYMENT & REVENUE LEDGER
             ================================================================ */}
          {activeTab === 'payments' && (
            <div>
              <div className="admin-view-header-row">
                <div className="header-title-block">
                  <h2>Razorpay Payment & Transaction Ledger</h2>
                  <p>Audited transaction log of all successful and pending credit purchases.</p>
                </div>

                <button className="start-assistant-primary-btn" onClick={loadPayments} style={{ padding: '8px 16px', fontSize: '0.84rem' }}>
                  Refresh
                </button>
              </div>

              <div className="admin-table-card">
                <table className="admin-modern-table">
                  <thead>
                    <tr>
                      <th>Payment ID / Order</th>
                      <th>Candidate</th>
                      <th>Package Plan</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentsList.length === 0 ? (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: '36px', color: 'var(--dash-text-muted)' }}>No payments logged yet.</td></tr>
                    ) : (
                      paymentsList.map(pay => (
                        <tr key={pay._id}>
                          <td>
                            <strong style={{ fontFamily: 'monospace', color: 'var(--dash-accent)' }}>
                              {pay.razorpayPaymentId || pay.razorpayOrderId || pay._id}
                            </strong>
                          </td>
                          <td>
                            <div className="table-user-info">
                              <span className="table-user-name">{pay.userId?.name || 'User'}</span>
                              <span className="table-user-email">{pay.userId?.email || 'N/A'}</span>
                            </div>
                          </td>
                          <td><strong>{pay.planName}</strong> ({Math.round(pay.credits || 0).toLocaleString()} Credits)</td>
                          <td>
                            <strong className="payment-amount-val">₹{pay.amount}</strong>
                          </td>
                          <td>
                            <span className={`status-pill status-${pay.status}`}>
                              {pay.status}
                            </span>
                          </td>
                          <td>{pay.createdAt ? new Date(pay.createdAt).toLocaleString() : 'N/A'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {paymentPagination.totalPages > 1 && (
                  <div className="admin-table-pagination">
                    <span>Page {paymentPagination.page} of {paymentPagination.totalPages}</span>
                    <div className="pagination-btns-row">
                      <button 
                        className="pagination-nav-btn"
                        disabled={paymentPagination.page <= 1}
                        onClick={() => setPaymentPagination(p => ({ ...p, page: p.page - 1 }))}
                      >
                        ‹ Prev
                      </button>
                      <button 
                        className="pagination-nav-btn"
                        disabled={paymentPagination.page >= paymentPagination.totalPages}
                        onClick={() => setPaymentPagination(p => ({ ...p, page: p.page + 1 }))}
                      >
                        Next ›
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ================================================================
              VIEW 6: SYSTEM CONFIGURATIONS
             ================================================================ */}
          {activeTab === 'config' && (
            <div>
              <div className="admin-view-header-row">
                <div className="header-title-block">
                  <h2>System & AI Engine Configurations</h2>
                  <p>Customize signup bonus credits, global promo messages, and inference rules.</p>
                </div>
              </div>

              <div className="admin-settings-card">
                <form onSubmit={handleSaveConfigs}>
                  <div className="settings-grid-layout">
                    
                    <div className="settings-field-box">
                      <label>1. Default Signup Bonus Credits</label>
                      <input 
                        type="number" 
                        value={systemConfigs.default_signup_credits}
                        onChange={(e) => setSystemConfigs({ ...systemConfigs, default_signup_credits: Number(e.target.value) })}
                      />
                      <span className="settings-hint-text">Complimentary credits granted automatically on new account signup.</span>
                    </div>

                    <div className="settings-field-box">
                      <label>2. Live Rate (Credits / Minute)</label>
                      <input 
                        type="number" 
                        value={systemConfigs.credits_per_minute}
                        onChange={(e) => setSystemConfigs({ ...systemConfigs, credits_per_minute: Number(e.target.value) })}
                      />
                      <span className="settings-hint-text">Deducted per live minute of active speech transcription & screen OCR.</span>
                    </div>

                    <div className="settings-field-box" style={{ gridColumn: 'span 2' }}>
                      <label>3. Global Top Announcement Banner Message</label>
                      <input 
                        type="text" 
                        value={systemConfigs.promo_banner_text}
                        onChange={(e) => setSystemConfigs({ ...systemConfigs, promo_banner_text: e.target.value })}
                        placeholder="e.g. Valid till 30th September: Get 50% Extra Credits on All Packages!"
                      />
                      <span className="settings-hint-text">Displayed on the landing page announcement banner.</span>
                    </div>

                    <div className="settings-field-box">
                      <label>4. Default AI Intelligence Model</label>
                      <select 
                        value={systemConfigs.default_ai_model}
                        onChange={(e) => setSystemConfigs({ ...systemConfigs, default_ai_model: e.target.value })}
                      >
                        <option value="gpt4">OpenAI GPT-4o Omniverse (Recommended)</option>
                        <option value="claude">Anthropic Claude 3.5 Sonnet</option>
                        <option value="gemini">Google Gemini 1.5 Pro</option>
                      </select>
                    </div>

                    <div className="settings-field-box">
                      <label>5. Platform Maintenance Mode</label>
                      <select 
                        value={systemConfigs.maintenance_mode ? 'true' : 'false'}
                        onChange={(e) => setSystemConfigs({ ...systemConfigs, maintenance_mode: e.target.value === 'true' })}
                      >
                        <option value="false">Normal Operations (Live)</option>
                        <option value="true">Maintenance Mode (Block New Launches)</option>
                      </select>
                    </div>

                    <div className="settings-field-box" style={{ gridColumn: 'span 2' }}>
                      <label>6. Windows App Download URL (.exe)</label>
                      <input 
                        type="url" 
                        value={systemConfigs.windows_app_url || ''}
                        onChange={(e) => setSystemConfigs({ ...systemConfigs, windows_app_url: e.target.value })}
                        placeholder="https://.../CrackIt-Setup-1.0.0.exe"
                      />
                      <span className="settings-hint-text">Direct executable download link for Windows 10/11 clients.</span>
                    </div>

                    <div className="settings-field-box" style={{ gridColumn: 'span 2' }}>
                      <label>7. macOS App Download URL (.dmg)</label>
                      <input 
                        type="url" 
                        value={systemConfigs.mac_app_url || ''}
                        onChange={(e) => setSystemConfigs({ ...systemConfigs, mac_app_url: e.target.value })}
                        placeholder="https://.../CrackIt-1.0.0-arm64.dmg"
                      />
                      <span className="settings-hint-text">Direct DMG installer download link for Apple Silicon / Intel Mac clients.</span>
                    </div>

                  </div>

                  <div style={{ marginTop: '28px', borderTop: '1px solid var(--dash-border)', paddingTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="submit" className="start-assistant-primary-btn">
                      Save System Configurations
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </div>

      </main>

      {/* ====================================================================
          MODALS
         ==================================================================== */}
      
      {/* 1. Credit Adjustment Modal */}
      {selectedUserForCredits && (
        <div className="coprep-modal-overlay" onClick={() => setSelectedUserForCredits(null)}>
          <div className="coprep-modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adjust Credits: {selectedUserForCredits.name}</h3>
              <button className="modal-close-btn" onClick={() => setSelectedUserForCredits(null)} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--dash-text-muted)', marginBottom: '16px' }}>
                Current Wallet Balance: <strong>{Math.round(selectedUserForCredits.credits || 0).toLocaleString()} Credits</strong>
              </p>

              <div className="form-group-item">
                <label className="input-label">Adjustment Mode</label>
                <select 
                  className="modern-text-input modern-select" 
                  value={creditMode} 
                  onChange={e => setCreditMode(e.target.value)}
                >
                  <option value="add">Add Credits (+)</option>
                  <option value="set">Set Exact Balance (=)</option>
                </select>
              </div>

              <div className="form-group-item">
                <label className="input-label">Credit Amount</label>
                <input 
                  type="number" 
                  className="modern-text-input" 
                  placeholder="e.g. 500" 
                  value={creditAmount}
                  onChange={e => setCreditAmount(e.target.value)}
                />
              </div>

              <div className="form-group-item">
                <label className="input-label">Audit Reason / Note</label>
                <input 
                  type="text" 
                  className="modern-text-input" 
                  placeholder="e.g. Manual top-up / Payment verification" 
                  value={creditReason}
                  onChange={e => setCreditReason(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-secondary-btn" onClick={() => setSelectedUserForCredits(null)}>Cancel</button>
              <button className="modal-primary-btn" onClick={handleSaveCredits}>Apply Credits</button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Full Transcript Viewer Modal */}
      {selectedInterviewForView && (() => {
        const rawTranscriptions = selectedInterviewForView.transcriptions && selectedInterviewForView.transcriptions.length > 0
          ? selectedInterviewForView.transcriptions
          : (selectedInterviewForView.messages || []).map(m => ({
              content: m.content,
              timestamp: m.timestamp,
              screenshot: m.screenshot || null,
              screenshots: m.screenshots || []
            }));

        const totalScreenshots = rawTranscriptions.reduce((acc, t) => {
          return acc + getEntryScreenshots(t).length;
        }, 0);

        return (
          <div 
            className={`coprep-modal-overlay transcript-modal-overlay ${isFullscreenTranscript ? 'is-fullscreen' : ''}`}
            onClick={() => setSelectedInterviewForView(null)}
          >
            <div 
              className={`coprep-modal-card transcript-fullscreen-card ${isFullscreenTranscript ? 'mode-true-fullscreen' : 'mode-theater'}`}
              onClick={e => e.stopPropagation()}
            >
              <div className="transcript-modal-header">
                <div className="transcript-header-left">
                  <div className="session-badge-row">
                    <span className="session-id-badge">SESSION: {selectedInterviewForView.sessionId}</span>
                    <span className={`session-status-pill status-${selectedInterviewForView.status || 'completed'}`}>
                      {(selectedInterviewForView.status || 'COMPLETED').toUpperCase()}
                    </span>
                    {selectedInterviewForView.config?.interviewType && (
                      <span className="session-type-pill">
                        {selectedInterviewForView.config.interviewType}
                      </span>
                    )}
                  </div>
                  <div className="candidate-meta-row">
                    <span className="candidate-avatar-mini">
                      {(selectedInterviewForView.userId?.name || 'U').slice(0, 2).toUpperCase()}
                    </span>
                    <span className="candidate-name-txt">
                      <strong>{selectedInterviewForView.userId?.name || 'Unknown Candidate'}</strong>
                      <span className="candidate-email-txt">({selectedInterviewForView.userId?.email || 'No email registered'})</span>
                    </span>
                    <span className="candidate-meta-divider">•</span>
                    <span className="candidate-stat-chip">
                      {rawTranscriptions.length} Exchanges
                    </span>
                    {totalScreenshots > 0 && (
                      <span className="candidate-stat-chip screenshot-chip">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                          <circle cx="12" cy="13" r="4"/>
                        </svg>
                        {totalScreenshots} {totalScreenshots === 1 ? 'Screenshot' : 'Screenshots'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="transcript-header-actions">
                  <button 
                    className="transcript-header-btn" 
                    title="Copy Full Session Transcript"
                    onClick={() => {
                      const text = rawTranscriptions.map((t, i) => {
                        const time = t.timestamp ? new Date(t.timestamp).toLocaleTimeString() : '';
                        let block = `[${time}] INTERVIEWER:\n${t.content || ''}\n`;
                        if (t.answer?.content) {
                          block += `\n[${t.answer.timestamp ? new Date(t.answer.timestamp).toLocaleTimeString() : time}] CRACKIT AI COPILOT:\n${t.answer.content}\n`;
                        }
                        return block;
                      }).join('\n----------------------------------------\n\n');
                      navigator.clipboard.writeText(text);
                      if (onSuccess) onSuccess('Full session transcript copied to clipboard');
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <span>Copy Transcript</span>
                  </button>

                  <button 
                    className="transcript-header-btn toggle-fullscreen-btn" 
                    title={isFullscreenTranscript ? 'Switch to Theater View' : 'Switch to Fullscreen'}
                    onClick={() => setIsFullscreenTranscript(prev => !prev)}
                  >
                    {isFullscreenTranscript ? (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
                        </svg>
                        <span>Theater View</span>
                      </>
                    ) : (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M15 3h6v6m-6 0l6-6M9 21H3v-6m0 0l6 6M21 9V3h-6m6 6l-6-6M3 9V3h6m-6 6l6-6"/>
                        </svg>
                        <span>Full Screen</span>
                      </>
                    )}
                  </button>

                  <button 
                    className="modal-close-btn transcript-close-btn" 
                    onClick={() => setSelectedInterviewForView(null)} 
                    aria-label="Close"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="transcript-fullscreen-body">
                <div className="transcript-dialogue-stream">
                  {rawTranscriptions && rawTranscriptions.length > 0 ? (
                    rawTranscriptions.map((t, idx) => {
                      const entryScreenshots = getEntryScreenshots(t);
                      return (
                        <div key={idx} className="transcript-exchange-card">
                          <div className="transcript-bubble-card bubble-interviewer-card">
                            <div className="bubble-meta-header">
                              <div className="bubble-speaker-identity">
                                <span className="speaker-avatar-dot interviewer-dot"></span>
                                <span className="speaker-title-label">INTERVIEWER</span>
                                <span className="bubble-exchange-index">#{idx + 1}</span>
                              </div>
                              <span className="bubble-timestamp-text">
                                {t.timestamp ? new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                              </span>
                            </div>

                            <div className="bubble-speech-text">
                              {t.content || <em style={{ color: 'rgba(255,255,255,0.4)' }}>[No verbal speech captured in this exchange]</em>}
                            </div>

                            {entryScreenshots.length > 0 && (
                              <div className="transcript-screenshots-attachment">
                                <div className="screenshots-attachment-header">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                    <circle cx="12" cy="13" r="4"/>
                                  </svg>
                                  <span>SCREEN CAPTURE ({entryScreenshots.length}) • CLICK TO EXPAND FULLSCREEN</span>
                                </div>
                                <div className="transcript-screenshots-grid">
                                  {entryScreenshots.map((imgSrc, sIdx) => (
                                    <div 
                                      key={sIdx} 
                                      className="screenshot-thumbnail-card"
                                      onClick={() => setPreviewImage({
                                        src: imgSrc,
                                        index: sIdx + 1,
                                        total: entryScreenshots.length,
                                        title: `SESSION: ${selectedInterviewForView.sessionId} • Screenshot #${sIdx + 1} (Exchange #${idx + 1})`
                                      })}
                                    >
                                      <div className="thumbnail-aspect-box">
                                        <img 
                                          src={imgSrc} 
                                          alt={`Screenshot ${sIdx + 1}`} 
                                        />
                                        <div className="screenshot-zoom-overlay">
                                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
                                            <circle cx="11" cy="11" r="8"/>
                                            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                            <line x1="11" y1="8" x2="11" y2="14"/>
                                            <line x1="8" y1="11" x2="14" y2="11"/>
                                          </svg>
                                          <span>Click to Expand</span>
                                        </div>
                                      </div>
                                      <div className="thumbnail-footer-caption">
                                        <span>Snapshot #{sIdx + 1}</span>
                                        <span className="view-full-tag">High-Res</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {t.answer && t.answer.content && (
                            <div className="transcript-bubble-card bubble-copilot-card">
                              <div className="bubble-meta-header">
                                <div className="bubble-speaker-identity">
                                  <span className="speaker-avatar-dot copilot-dot"></span>
                                  <span className="speaker-title-label copilot-label">CRACKIT AI COPILOT</span>
                                  <span className="copilot-ai-model-tag">
                                    {selectedInterviewForView.config?.aiModel || 'GPT-4o Omniverse'}
                                  </span>
                                </div>
                                <div className="bubble-meta-actions">
                                  <button 
                                    className="copy-answer-pill-btn"
                                    onClick={() => {
                                      navigator.clipboard.writeText(t.answer.content);
                                      if (onSuccess) onSuccess('AI suggestion copied to clipboard');
                                    }}
                                    title="Copy suggestion"
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                    </svg>
                                    <span>Copy</span>
                                  </button>
                                  <span className="bubble-timestamp-text">
                                    {t.answer.timestamp ? new Date(t.answer.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                                  </span>
                                </div>
                              </div>

                              <div 
                                className="bubble-markdown-rendered"
                                dangerouslySetInnerHTML={{ __html: renderMarkdown(t.answer.content) }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="empty-transcript-slate">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255, 255, 255, 0.25)" strokeWidth="1.5">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <h4>No Audio Feeds Recorded</h4>
                      <p>No audio transcript dialogue or screenshots were captured during this interview session.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="transcript-modal-footer">
                <div className="transcript-footer-left">
                  <span className="footer-metric-pill">
                    <strong>{rawTranscriptions.length}</strong> Dialogue Blocks
                  </span>
                  <span className="footer-metric-pill">
                    <strong>{totalScreenshots}</strong> Screen Snapshots
                  </span>
                  {selectedInterviewForView.startedAt && (
                    <span className="footer-metric-pill">
                      Started: {new Date(selectedInterviewForView.startedAt).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="transcript-footer-right">
                  <button 
                    className="modal-primary-btn transcript-done-btn" 
                    onClick={() => setSelectedInterviewForView(null)}
                  >
                    Close Viewer
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 3. Pricing Plan Create / Edit Modal */}
      {(isCreatingPlan || selectedPlanForEdit) && (
        <div className="coprep-modal-overlay" onClick={() => { setIsCreatingPlan(false); setSelectedPlanForEdit(null); }}>
          <div className="coprep-modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isCreatingPlan ? 'Create New Pricing Tier' : `Edit Plan: ${selectedPlanForEdit?.name}`}</h3>
              <button className="modal-close-btn" onClick={() => { setIsCreatingPlan(false); setSelectedPlanForEdit(null); }} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleSavePlan}>
              <div className="modal-body">
                <div className="form-group-item">
                  <label className="input-label">Plan Name</label>
                  <input type="text" name="name" className="modern-text-input" defaultValue={selectedPlanForEdit?.name || ''} required />
                </div>
                <div className="form-group-item">
                  <label className="input-label">Price (INR ₹)</label>
                  <input type="number" step="1" name="price" className="modern-text-input" placeholder="e.g. 799" defaultValue={selectedPlanForEdit ? selectedPlanForEdit.price : ''} required />
                </div>
                <div className="form-group-item">
                  <label className="input-label">Copilot Credits</label>
                  <input type="number" name="credits" className="modern-text-input" defaultValue={selectedPlanForEdit?.credits || ''} required />
                </div>
                <div className="form-group-item">
                  <label className="input-label">Ribbon Badge (Optional)</label>
                  <input type="text" name="badge" className="modern-text-input" placeholder="e.g. MOST POPULAR" defaultValue={selectedPlanForEdit?.badge || ''} />
                </div>
                <div className="form-group-item">
                  <label className="input-label">Display Order</label>
                  <input type="number" name="displayOrder" className="modern-text-input" defaultValue={selectedPlanForEdit?.displayOrder || 0} />
                </div>
                <div style={{ display: 'flex', gap: '24px', marginTop: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: 'var(--dash-text-main)', cursor: 'pointer' }}>
                    <input type="checkbox" name="isPopular" defaultChecked={selectedPlanForEdit?.isPopular} />
                    Popular Plan Ribbon
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: 'var(--dash-text-main)', cursor: 'pointer' }}>
                    <input type="checkbox" name="isActive" defaultChecked={selectedPlanForEdit ? selectedPlanForEdit.isActive : true} />
                    Active in Store
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="modal-secondary-btn" onClick={() => { setIsCreatingPlan(false); setSelectedPlanForEdit(null); }}>Cancel</button>
                <button type="submit" className="modal-primary-btn">Save Plan</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Delete Confirmation Modal */}
      {deleteConfirmTarget && (
        <div className="coprep-modal-overlay" onClick={() => setDeleteConfirmTarget(null)}>
          <div className="coprep-modal-card" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button className="modal-close-btn" onClick={() => setDeleteConfirmTarget(null)} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: 'var(--dash-text-main)' }}>
                Are you sure you want to permanently delete this {deleteConfirmTarget.type} (<strong>{deleteConfirmTarget.name}</strong>)? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-secondary-btn" onClick={() => setDeleteConfirmTarget(null)}>Cancel</button>
              <button className="modal-primary-btn" style={{ background: '#ef4444' }} onClick={handleConfirmDelete}>
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. Screenshot Lightbox Overlay */}
      {previewImage && (
        <div 
          className="coprep-image-lightbox-overlay" 
          onClick={() => setPreviewImage(null)}
        >
          <div className="lightbox-content-box" onClick={e => e.stopPropagation()}>
            <div className="lightbox-header-bar">
              <div className="lightbox-title-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00f0ff" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span className="lightbox-title-text">{previewImage.title || 'Screen Capture'}</span>
              </div>
              <div className="lightbox-actions-group">
                <a 
                  href={previewImage.src} 
                  download={`crackit-capture-${Date.now()}.png`}
                  className="lightbox-btn-download"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  <span>Save Original</span>
                </a>
                <button 
                  className="modal-close-btn lightbox-btn-close" 
                  onClick={() => setPreviewImage(null)}
                  aria-label="Close Preview"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="lightbox-image-stage">
              <img 
                src={previewImage.src} 
                alt="Full screen capture inspection" 
                className="lightbox-full-img" 
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Admin;
